<template>
  <v-dialog v-model="openDialog" width="600px" class="dialog">
    <template v-slot:activator="{ on }">
      <mf-button class="pr-3" label="Exportar dados de navegação" text color="marca-0-l40" icon="download" v-on:click.native="on.click" />
    </template>
    <v-card>
      <v-card-title>
        <span>Exportar dados de navegação agregados</span>
      </v-card-title>
      <v-card-subtitle class="pt-3">
        <span class="description-label"
          >A plataforma Mercafacil registra todas as páginas e rotas acessadas por um usuário. Este relatório exporta a quantidade de navegações na plataforma
          para cada usuário, dentro do período selecionado, com o agrupamento selecionado.</span
        >
      </v-card-subtitle>
      <v-card-text>
        <v-row>
          <v-col cols="6">
            <mf-date-range-picker :initial-date.sync="startDate" :final-date.sync="endDate" label="Período" dense hide-details />
          </v-col>
          <v-col cols="6">
            <mf-select v-model="selectedGroupBy" :items="groupByOptions" label="Agrupar por" item-text="text" item-value="value" dense hide-details />
          </v-col>
        </v-row>
        <mf-info-card
          v-if="!validPeriodSize"
          class="mt-4"
          description="O intervalo selecionado é muito grande. Por favor, selecione um intervalo com no máximo 100 períodos."
          icon="warning"
          variant="warning"
        />
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <mf-button class="pr-3" label="Cancelar" outlined @click="openDialog = false" />
        <export-data :disabled="!validPeriodSize" :data-fetcher="getDataToExport" :filename="`navegacao_usuarios_agregados`" />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { QUERY_GET_AGGREGATED_NAVIGATION_LOGS } from '@/modules/usuarios/graphql'
export default {
  name: 'ExportAggregatedNavigationModal',
  components: {
    ExportData: () => import('@/components/export-data/ExportData.vue'),
  },
  data: () => ({
    openDialog: false,
    selectedGroupBy: 'MONTH',
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10),
    endDate: new Date().toISOString().slice(0, 10),
    validPeriodSize: true,
  }),
  computed: {
    groupByOptions() {
      return [
        { text: 'Dia', value: 'DAY' },
        { text: 'Semana', value: 'WEEK' },
        { text: 'Mês', value: 'MONTH' },
        { text: 'Ano', value: 'YEAR' },
      ]
    },
  },
  watch: {
    startDate() {
      this.validatePeriodSize()
    },
    endDate() {
      this.validatePeriodSize()
    },
    selectedGroupBy() {
      this.validatePeriodSize()
    },
  },
  methods: {
    async getDataToExport() {
      const headers = ['E-mail']
      const rows = []
      const { data } = await this.$apollo.query({
        query: QUERY_GET_AGGREGATED_NAVIGATION_LOGS,
        variables: {
          startDate: this.startDate,
          endDate: this.endDate,
          groupBy: this.selectedGroupBy,
        },
        fetchPolicy: 'no-cache',
      })
      const exampleMetrics = data.aggregatePlatformUserTrackMetrics?.[0]?.metrics
      if (!exampleMetrics?.length) {
        return {
          headers: ['Sem dados de navegação para o período selecionado'],
          rows: [],
        }
      }
      exampleMetrics.forEach((metric) => {
        if (metric.startDate === metric.endDate) {
          headers.push(metric.startDate)
        } else {
          headers.push(`${metric.startDate} - ${metric.endDate}`)
        }
      })
      data.aggregatePlatformUserTrackMetrics.forEach((metric) => {
        const row = [metric.user.email]
        metric.metrics.forEach((metric) => {
          row.push(metric.count)
        })
        rows.push(row)
      })
      return {
        headers,
        rows,
      }
    },
    validatePeriodSize() {
      let periods = 0
      const current = new Date(this.startDate)
      const endDate = new Date(this.endDate)
      // eslint-disable-next-line no-unmodified-loop-condition
      while (current <= endDate) {
        const periodStart = new Date(current)
        let periodEnd

        switch (this.selectedGroupBy) {
          case 'DAY': {
            periodEnd = new Date(current)
            periodEnd.setHours(23, 59, 59, 999)
            current.setDate(current.getDate() + 1)
            break
          }
          case 'WEEK': {
            // Start of week (Sunday)
            periodStart.setDate(current.getDate() - current.getDay())
            periodStart.setHours(0, 0, 0, 0)

            periodEnd = new Date(periodStart)
            periodEnd.setDate(periodStart.getDate() + 6)
            periodEnd.setHours(23, 59, 59, 999)
            current.setDate(current.getDate() + 7)
            break
          }
          case 'MONTH': {
            periodStart.setDate(1)
            periodStart.setHours(0, 0, 0, 0)
            periodEnd = new Date(periodStart)
            periodEnd.setMonth(periodStart.getMonth() + 1, 0)
            periodEnd.setHours(23, 59, 59, 999)
            current.setMonth(current.getMonth() + 1, 1)
            break
          }
          case 'YEAR': {
            periodStart.setMonth(0, 1)
            periodStart.setHours(0, 0, 0, 0)
            periodEnd = new Date(periodStart)
            periodEnd.setFullYear(periodStart.getFullYear() + 1, 0, 0)
            periodEnd.setHours(23, 59, 59, 999)
            current.setFullYear(current.getFullYear() + 1, 0, 1)
            break
          }
          default: {
            periodEnd = new Date(current)
            current.setDate(current.getDate() + 1)
          }
        }
        periods++

        // Prevent infinite loop
        if (periods > 100) {
          this.validPeriodSize = false
          return
        }
        this.validPeriodSize = true
      }
    },
  },
}
</script>
